<template>
    <div
        v-if="!isLoading"
        class="row q-col-gutter-md items-stretch"
    >
        <div
            class="col-12 col-md-4 q-pa-sm"
        >
            <div class="column full-height q-px-sm">
                <div class="col column justify-center">
                    <div class="row q-col-gutter-sm">
                        <div class="col-6 col-md-12">
                            <q-card
                                flat
                                bordered
                                class="KpiCard"
                                :style="{ borderLeft: `4px solid ${goodBorder}` }"
                            >
                                <q-card-section class="q-pa-xs">
                                    <div class="text-caption text-grey-7">
                                        {{ $t('visualization.conformance.fulfillment.correct') }}
                                    </div>
                                    <div class="row items-end justify-between no-wrap">
                                        <div class="text-h5 text-weight-bold" :style="{ color: goodFill }">
                                            {{ correctPercentage }}%
                                        </div>
                                        <div class="text-caption">
                                            {{ App ? App.numberLocationFormat(correctAbsolute) : correctAbsolute }}
                                        </div>
                                    </div>
                                </q-card-section>
                            </q-card>
                        </div>
                        <div class="col-6 col-md-12">
                            <q-card
                                flat
                                bordered
                                class="KpiCard"
                                :style="{ borderLeft: `4px solid ${badBorder}` }"
                            >
                                <q-card-section class="q-pa-xs">
                                    <div class="text-caption text-grey-7">
                                        {{ $t('visualization.conformance.fulfillment.incorrect') }}
                                    </div>
                                    <div class="row items-end justify-between no-wrap">
                                        <div class="text-h5 text-weight-bold" :style="{ color: badFill }">
                                            {{ incorrectPercentage }}%
                                        </div>
                                        <div class="text-caption">
                                            {{ App ? App.numberLocationFormat(incorrectAbsolute) : incorrectAbsolute }}
                                        </div>
                                    </div>
                                </q-card-section>
                            </q-card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="col-12 col-md-8 q-pa-sm"
        >
            <div class="column full-height">
                <div class="text-subtitle1 text-center q-mb-xs">
                    {{ $t('visualization.conformance.fulfillment.percentageTimeTitle') }}
                </div>

                <div class="col OverviewBarChart">
                    <q-resize-observer @resize="handleChartResize" />
                    <v-chart
                        ref="barChart"
                        class="chart"
                        :option="barChartData"
                        autoresize
                    />
                </div>

                <div class="row items-center justify-center q-mt-xs">
                    <QSelect
                        v-model="timeModel"
                        dense
                        outlined
                        :options="timeUnits"
                        :label="$t('visualization.conformance.fulfillment.selectUnit')"
                        style="width: 240px;"
                        @update:modelValue="changeTimeUnit"
                    />
                </div>
            </div>
        </div>
    </div>
    <div
        v-else
        class="row"
    >
        <div class="col">
            <QLinearProgress
                indeterminate
            />
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest,
} from '@/api'
import { filtersStorageMixin, validatorsMixin } from '@/mixins'
import echartsColors from '@/charts/echartsColors'

const barChartOption = {
    title: {
        text: '',
        left: 'center',
    },
    legend: {
        orient: 'vertical',
        left: 'right',
        top: 'top',
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'shadow',
        },
    },
    xAxis: {
        type: 'value',
        splitLine: {
            lineStyle: {
                type: 'dashed',
            },
        },
        nameLocation: 'middle',
        nameTextStyle: {
            align: 'left',
            verticalAlign: 'top',
        },
    },
    yAxis: {
        type: 'value',
    },
    dataZoom: [
        {
            type: 'slider',
            show: true,
            start: 80,
            end: 100,
            handleSize: 8,
        },
    ],
}

export default {
    name: 'OverviewComponent',
    components: {
    },
    mixins: [filtersStorageMixin, validatorsMixin],
    inject: ['App'],
    props: {
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        modelId: VueTypes.string,
    },
    data() {
        return {
            barChartData: barChartOption,
            isLoading: false,
            timeModel: undefined,
            correctAbsolute: 0,
            incorrectAbsolute: 0,
            correctPercentage: 0,
            incorrectPercentage: 0,
        }
    },
    computed: {
        goodFill() {
            return echartsColors.semantic.sla.good.fill
        },
        goodBorder() {
            return echartsColors.semantic.sla.good.border
        },
        badFill() {
            return echartsColors.semantic.sla.bad.fill
        },
        badBorder() {
            return echartsColors.semantic.sla.bad.border
        },
        timeUnits() {
            return [
                {
                    label: this.$t('visualization.conformance.fulfillment.months'),
                    value: 'month',
                },
                {
                    label: this.$t('visualization.conformance.fulfillment.weeks'),
                    value: 'week',
                },
                {
                    label: this.$t('visualization.conformance.fulfillment.days'),
                    value: 'day',
                },
            ]
        },
    },
    watch: {
        modelId() {
            this.setConformanceInfo()
        },
    },
    mounted() {
        [this.timeModel] = this.timeUnits
        this.setConformanceInfo()
    },
    methods: {
        handleChartResize() {
            if (!this.$refs.barChart) return
            setTimeout(() => {
                this.$refs.barChart?.resize?.()
            }, 0)
        },
        async fetchConformanceInfo() {
            if (!this.modelId) return
            this.isLoading = true
            const { processId } = this
            const { modelId } = this
            const timeUnit = this.timeModel.value
            apiRequest(Api().conformance.checkModel({
                datasetId: processId,
                bpmnId: modelId,
                params: this.buildVisualizationParams({ timeSplit: timeUnit }),
            }))
                .then((data) => {
                    this.correctAbsolute = data.all.correct.absolute
                    this.incorrectAbsolute = data.all.incorrect.absolute
                    const total = this.correctAbsolute + this.incorrectAbsolute
                    this.correctPercentage = total > 0 ? Math.round((this.correctAbsolute / total) * 100) : 0
                    this.incorrectPercentage = total > 0 ? Math.round((this.incorrectAbsolute / total) * 100) : 0

                    const sortedData = [...Object.entries(data.data)].sort((a, b) => {
                        if (a < b) {
                            return -1
                        }
                        if (a > b) {
                            return 1
                        }
                        return 0
                    })
                    const timeIntervals = sortedData.map((x) => x[0])
                    const validPercentage = sortedData.map((x) => Math.round(x[1].correct.percentage * 100))
                    const invalidPercentage = sortedData.map((x) => Math.round(x[1].incorrect.percentage * 100))
                    this.barChartData = {
                        ...this.barChartData,
                        xAxis: [
                            {
                                type: 'category',
                                axisTick: { show: false },
                                data: timeIntervals,
                            },
                        ],
                        dataZoom: [
                            {
                                start: timeIntervals.length > 15 ? 80 : 0,
                                end: 100,
                                handleSize: 8,
                            },
                        ],
                        series: [
                            {
                                name: this.$t('visualization.conformance.fulfillment.validPercentageTime'),
                                type: 'bar',
                                barGap: 0,
                                emphasis: {
                                    focus: 'series',
                                },
                                itemStyle: {
                                    borderColor: this.goodBorder,
                                    color: this.goodFill,
                                    borderWidth: 1,
                                    borderRadius: [3, 3, 3, 3],
                                },
                                data: validPercentage,
                            },
                            {
                                name: this.$t('visualization.conformance.fulfillment.invalidPercentageTime'),
                                type: 'bar',
                                emphasis: {
                                    focus: 'series',
                                },
                                itemStyle: {
                                    borderColor: this.badBorder,
                                    color: this.badFill,
                                    borderWidth: 1,
                                    borderRadius: [3, 3, 3, 3],
                                },
                                data: invalidPercentage,
                            },
                        ],
                    }

                    this.$nextTick(() => {
                        this.handleChartResize()
                    })
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        setConformanceInfo() {
            if (!this.modelId) return
            this.fetchConformanceInfo()
        },
        changeTimeUnit() {
            if (!this.modelId) return
            this.fetchConformanceInfo()
        },
    },
}
</script>
<style scoped lang="scss">
.chart {
    min-width: 0;
    height: 220px;
    width: 100%;
}

.OverviewBarChart {
    min-height: 0;
}

.KpiCard {
    border-left-width: 4px;
    border-left-style: solid;
}
</style>
